import {
  Avatar,
  Badge,
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  NavbarMenu,
  NavbarMenuItem,
  NavbarMenuToggle,
} from '@heroui/react';
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useAdminStatus } from '../hooks/useAdminStatus';
import LocaleSelector from './LocaleSelector';

/** Routes that require authentication - redirect to home on logout */
const PROTECTED_PATH_PREFIXES = ['/account', '/admin', '/profile'];

const isOnProtectedRoute = (pathname: string) =>
  PROTECTED_PATH_PREFIXES.some(
    prefix => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );

const Header: React.FC = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { getCartItemCount } = useCart();
  const { isAdmin } = useAdminStatus();
  const navigate = useNavigate();
  const location = useLocation();
  const totalItems = getCartItemCount();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    if (isOnProtectedRoute(location.pathname)) {
      navigate('/', { replace: true });
    }
  };

  const menuItems = [
    { name: 'Home', href: '/' },
    { name: 'Browse', href: '/browse' },
    { name: 'Search', href: '/search' },
    { name: 'Track Order', href: '/track-order' },
  ];

  // Add admin link to menu items if user is admin
  const navigationItems = isAdmin
    ? [...menuItems, { name: 'Admin', href: '/admin' }]
    : menuItems;

  return (
    <Navbar
      isMenuOpen={isMenuOpen}
      onMenuOpenChange={setIsMenuOpen}
      className='bg-background/70 backdrop-blur-md'
      maxWidth='xl'
    >
      <NavbarContent>
        <NavbarMenuToggle
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          className='md:hidden'
        />
        <NavbarBrand>
          <Link
            to='/'
            className='font-bold text-xl text-primary hover:text-primary-600 transition-colors flex items-center gap-2'
          >
            🎲 <span className='text-foreground'>CodedGhostMinis</span>
          </Link>
        </NavbarBrand>
      </NavbarContent>

      <NavbarContent className='hidden md:flex gap-4' justify='center'>
        {navigationItems.map(item => (
          <NavbarItem key={item.name}>
            <Link
              to={item.href}
              className={`text-foreground hover:text-primary transition-colors font-medium ${
                item.name === 'Admin'
                  ? 'bg-primary-50 px-3 py-1 rounded-md'
                  : ''
              }`}
            >
              {item.name}
            </Link>
          </NavbarItem>
        ))}
      </NavbarContent>

      <NavbarContent justify='end'>
        <NavbarItem>
          <Badge
            content={totalItems}
            color='danger'
            variant='solid'
            isInvisible={totalItems === 0}
            size='sm'
          >
            <Button
              isIconOnly
              variant='light'
              as={Link}
              to='/cart'
              className='text-default-500'
            >
              🛒
            </Button>
          </Badge>
        </NavbarItem>

        <NavbarItem>
          <LocaleSelector />
        </NavbarItem>

        {isAuthenticated && user ? (
          <NavbarItem>
            <Dropdown placement='bottom-end'>
              <DropdownTrigger>
                <Avatar
                  isBordered
                  as='button'
                  className='transition-transform'
                  color='primary'
                  name={user.firstName}
                  size='sm'
                  src=''
                />
              </DropdownTrigger>
              <DropdownMenu aria-label='Profile Actions' variant='flat'>
                <DropdownItem key='profile' className='h-14 gap-2'>
                  <p className='font-semibold'>Signed in as</p>
                  <p className='font-semibold'>{user.email}</p>
                </DropdownItem>
                <DropdownItem
                  key='account'
                  onClick={() => navigate('/account')}
                >
                  My Account
                </DropdownItem>
                {isAdmin ? (
                  <DropdownItem
                    key='admin'
                    onClick={() => navigate('/admin')}
                    className='text-primary'
                  >
                    🛡️ Admin Panel
                  </DropdownItem>
                ) : null}
                <DropdownItem
                  key='logout'
                  color='danger'
                  onClick={handleLogout}
                >
                  Log Out
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </NavbarItem>
        ) : (
          <NavbarItem className='hidden lg:flex'>
            <Link
              to='/login'
              className='text-foreground hover:text-primary transition-colors'
            >
              Login
            </Link>
          </NavbarItem>
        )}

        {!isAuthenticated && (
          <NavbarItem>
            <Button
              as={Link}
              to='/register'
              color='primary'
              variant='flat'
              size='sm'
            >
              Sign Up
            </Button>
          </NavbarItem>
        )}
      </NavbarContent>

      <NavbarMenu>
        {navigationItems.map((item, index) => (
          <NavbarMenuItem key={`${item.name}-${index}`}>
            <Link
              className={`w-full text-foreground hover:text-primary transition-colors ${
                item.name === 'Admin' ? 'font-semibold text-primary' : ''
              }`}
              to={item.href}
              onClick={() => setIsMenuOpen(false)}
            >
              {item.name === 'Admin' ? '🛡️ Admin Panel' : item.name}
            </Link>
          </NavbarMenuItem>
        ))}
        {!isAuthenticated && (
          <>
            <NavbarMenuItem>
              <Link
                className='w-full text-foreground hover:text-primary transition-colors'
                to='/login'
                onClick={() => setIsMenuOpen(false)}
              >
                Login
              </Link>
            </NavbarMenuItem>
            <NavbarMenuItem>
              <Link
                className='w-full text-primary font-medium'
                to='/register'
                onClick={() => setIsMenuOpen(false)}
              >
                Sign Up
              </Link>
            </NavbarMenuItem>
          </>
        )}
      </NavbarMenu>
    </Navbar>
  );
};

export default Header;
